import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useLanguage } from "./Language";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { ForgotPINDialog } from "./ForgotPINDialog";
import { LoginCredentials } from "./LoginDialog";
import { ForgotPasswordButton } from "./ForgotPasswordButton";
import { useForm, SubmitHandler } from "react-hook-form";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

export interface LocalLoginFormProps {
  loginMessage?: string;
  isGoogleLoginEnabled: boolean;
  accountNotLinked: string;
  /**
   * "isStationLocationSet"
   */
  currentBranchId?: number;
  displayGuardianLogin: boolean;
  showRememberMe: boolean;
  useForgotPasswordLink: boolean;
  showResetPINLink: boolean;
  showLoginError: boolean;
  isKids: boolean;
  onClickClose: () => void;
  onSubmitLogin: (creds: LoginCredentials) => void;
  onClickBack?: (event: LoginFormInput) => void;
  defaultUsername?: string;
  defaultPassword?: string;
}

export interface LocalLoginCredentials extends LoginCredentials {
  email: string | null;
  linkGoogleAccount: "true" | "false";
  pin: string;
  rememberMe: boolean;
}

export interface LoginFormInput {
  barcode: string;
  password: string;
  rememberMe: boolean;
}

export const LocalLoginForm: React.FC<LocalLoginFormProps> = (props) => {
  const {
    onClickClose,
    loginMessage,
    isGoogleLoginEnabled = false,
    accountNotLinked,
    currentBranchId,
    showRememberMe = false,
    showResetPINLink = false,
    useForgotPasswordLink = false,
    displayGuardianLogin = false,
    showLoginError = false,
    onSubmitLogin,
    onClickBack,
    isKids = false,
  } = props;
  const { translate } = useLanguage();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormInput>({
    mode: "onBlur",
    defaultValues: {
      barcode: props.defaultUsername,
      password: props.defaultPassword,
      rememberMe: false,
    },
  });

  const [forgotPINOpen, setForgotPINOpen] = React.useState(false);

  const onSubmit: SubmitHandler<LoginFormInput> = (data) => {
    const { barcode, password, rememberMe } = data;
    const payload: LocalLoginCredentials = {
      username: barcode,
      password,
      pin: password,
      rememberMe,
      localLoginOnly: true,
      linkGoogleAccount: (accountNotLinked || "").length > 0 ? "true" : "false",
      email: accountNotLinked,
    };
    onSubmitLogin(payload);
  };

  const onClickForgotPIN = () => {
    setForgotPINOpen(true);
  };

  return (
    <React.Fragment>
      {forgotPINOpen && (
        <ForgotPINDialog
          onClose={() => setForgotPINOpen(false)}
        ></ForgotPINDialog>
      )}
      <DialogTitle sx={{ display: "flex" }}>
        <Box sx={{ width: "40px", height: "40px" }}>
          {!accountNotLinked && displayGuardianLogin && (
            <IconButton
              aria-label="Back to Student Login"
              onClick={() => {
                const currentValues = getValues();
                console.log("Current form values:", currentValues);
                onClickBack(currentValues);
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}
        </Box>
        <Box sx={{ textAlign: "center", flexGrow: 1 }}>
          {translate("loginDialogTitle")}
        </Box>
        <Box sx={{ width: "40px", height: "40px" }}></Box>
      </DialogTitle>

      <DialogContent sx={{ minWidth: "320px" }}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack gap={2} paddingTop={1}>
            <DialogContentText textAlign="center">
              {translate("dualLoginTitle")}
            </DialogContentText>

            {showLoginError && (
              <Alert severity="error">
                {isKids
                  ? translate("kidsLoginErrorMessage")
                  : translate("loginErrorMessage")}
              </Alert>
            )}
            {isGoogleLoginEnabled && accountNotLinked && (
              <Alert severity="error">
                {translate("loginFormGoogleLinkMessage", {
                  EMAILADDRESS: accountNotLinked,
                })}
                <input
                  type="hidden"
                  name="linkGoogleAccount"
                  id="linkGoogleAccount"
                  value={accountNotLinked ? "true" : "false"}
                />
              </Alert>
            )}
            {loginMessage && <Alert severity="info">{loginMessage}</Alert>}
            {/* TODO autocorrect and autocapitalize "off" */}
            <TextField
              name="barcode"
              {...register("barcode", { required: true })}
              error={!!errors.barcode}
              helperText={
                errors.barcode ? translate("requiredFieldError") : " "
              }
              required
              autoFocus
              label={
                isKids
                  ? translate("kidsLoginUsername")
                  : `${translate("barcode")} ${translate("or")} ${translate(
                      "ezUsername"
                    )}`
              }
              id="login-form-username"
            ></TextField>
            <TextField
              name="password"
              {...register("password", { required: true })}
              error={!!errors.password}
              helperText={
                errors.password ? translate("requiredFieldError") : " "
              }
              required
              label={
                isKids
                  ? translate("kidsLoginPassword")
                  : `${translate("pin")} ${translate("or")} ${translate(
                      "ezPassword"
                    )}`
              }
              type="password"
              id="login-form-password"
            ></TextField>
            {!isKids && (showResetPINLink || useForgotPasswordLink) && (
              <Box>
                {showResetPINLink && (
                  <Button onClick={onClickForgotPIN}>
                    {translate("forgotPIN")}
                  </Button>
                )}
                {useForgotPasswordLink && <ForgotPasswordButton />}
              </Box>
            )}
            {showRememberMe && !currentBranchId && (
              <FormControlLabel
                {...register("rememberMe")}
                control={<Checkbox />}
                label={translate("rememberMe")}
              />
            )}

            <Button type="submit" variant="contained">
              {isKids ? translate("kidsLoginButton") : translate("logon")}
            </Button>
            <Box sx={{ textAlign: "center" }}>
              <Button onClick={onClickClose}>
                {isKids
                  ? translate("kidsLoginCloseButton")
                  : translate("cancel")}
              </Button>
            </Box>

            {isGoogleLoginEnabled && !accountNotLinked && (
              <React.Fragment>
                <Divider>
                  <Typography variant="overline">{translate("or")}</Typography>
                </Divider>
                <Box sx={{ textAlign: "center" }}>
                  <GoogleSignInButton
                  // TODO: see if we can prevent this from being stored in history
                    onClick={() => (location.href = "/oauth2/callback")}
                  ></GoogleSignInButton>
                </Box>
              </React.Fragment>
            )}
          </Stack>
        </form>
      </DialogContent>
    </React.Fragment>
  );
};
